import React, { useState } from 'react';
import { Menu, X, Package, Phone, Linkedin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Desktop shows a focused set; the mobile menu keeps every destination.
  const desktopLinks = [
    { name: 'Products', href: '/products' },
    { name: 'Projects', href: '/projects' },
    { name: 'About', href: '/about' },
    { name: 'Approvals', href: '/approvals' },
    { name: 'Blog', href: '/blog' },
  ];

  const mobileLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Products', href: '/products' },
    { name: 'Projects', href: '/projects' },
    { name: 'Approvals', href: '/approvals' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
    { name: 'Blog', href: '/blog' },
    { name: 'Media', href: '/media' },
  ];

  // Section-aware active state: /products/niche-panels highlights Products.
  const isActive = (href) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const handleLinkClick = (name, href) => {
    setMobileMenuOpen(false);
    // If we are already on the page, scroll to top
    if (location.pathname === href) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b border-white/10 ${
        scrolled ? 'bg-hillpoint/95 backdrop-blur-md py-2 shadow-lg' : 'bg-hillpoint/80 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] sm:pl-6 sm:pr-6 lg:pl-8 lg:pr-8">
        <div className="flex justify-between items-center gap-2 min-w-0">
          {/* Logo */}
          <div className="flex-shrink-0 min-w-0 transition-all duration-300 max-w-[52%] sm:max-w-none">
            <Link to="/" onClick={() => handleLinkClick('Home', '/')} className="flex items-center">
              <picture>
                <source srcSet="/hillpoint-logo.webp" type="image/webp" />
                <img
                  src="/hillpoint-logo.png"
                  alt="HILLPOINT - Acoustics Enhanced"
                  width={528}
                  height={120}
                  className={`transition-all duration-500 object-contain ${
                      scrolled ? 'h-10' : 'h-12 md:h-14'
                  }`}
                />
              </picture>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-10">
            <nav className="flex items-center gap-6 xl:gap-10">
                {desktopLinks.map((link) => (
                    <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => handleLinkClick(link.name, link.href)}
                    className="relative group py-2"
                    aria-current={isActive(link.href) ? 'page' : undefined}
                    >
                    <span className={`text-sm font-medium transition-colors duration-300 group-hover:text-gold tracking-wide uppercase ${
                        isActive(link.href) ? 'text-gold' : 'text-white/90'
                    }`}>
                        {link.name}
                    </span>
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gold transition-all duration-300 group-hover:w-full ${
                        isActive(link.href) ? 'w-full' : 'w-0'
                    }`}></span>
                    </Link>
                ))}
            </nav>

            {/* Header CTA */}
            <Link
              to="/contact"
              onClick={() => handleLinkClick('Contact', '/contact')}
              className="bg-gold text-hillpoint text-sm font-bold uppercase tracking-[0.15em] px-6 py-3 border border-gold hover:bg-white hover:border-white transition-colors duration-300"
            >
              Get in Touch
            </Link>
          </div>

          {/* Mobile Navigation Icons */}
          <div className="lg:hidden flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link
              to="/products"
              aria-label="Products"
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors duration-300"
              onClick={() => handleLinkClick('Products', '/products')}
            >
              <Package size={20} />
            </Link>

            <Link
              to="/contact"
              aria-label="Contact"
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors duration-300"
              onClick={() => handleLinkClick('Contact', '/contact')}
            >
              <Phone size={20} />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              className="p-2 rounded-lg text-white hover:text-gold transition-colors duration-300"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 w-full bg-hillpoint/95 backdrop-blur-xl border-b border-white/10 transition-all duration-300 ease-in-out overflow-hidden ${
            mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="py-6 space-y-2 pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] sm:pl-6 sm:pr-6">
          {mobileLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleLinkClick(link.name, link.href)}
                aria-current={isActive(link.href) ? 'page' : undefined}
                className={`block px-4 py-3 text-lg font-medium hover:text-gold hover:bg-white/5 rounded-lg transition-all duration-300 border-l-2 hover:border-gold ${
                  isActive(link.href) ? 'text-gold border-gold bg-white/5' : 'text-white/90 border-transparent'
                }`}
              >
                {link.name}
              </Link>
          ))}
          {/* Mobile LinkedIn Link */}
          <a
            href="https://www.linkedin.com/company/hillpoint"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-3 text-lg font-medium text-white/90 hover:text-gold hover:bg-white/5 rounded-lg transition-all duration-300 border-l-2 border-transparent hover:border-gold flex items-center gap-3"
          >
            <Linkedin size={20} />
            LinkedIn
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
